/**
 * Publieke reviewpagina — kop en intro uit CMS; vaste hulpsecties voor structuur en SEO-body.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Calculator, FileSpreadsheet, MessageCircle, Sparkles } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';
import { LEGAL_PRODUCT_NAME } from '../lib/legal-site-info';

const HIGHLIGHTS = [
  {
    Icon: Calculator,
    title: 'Begroten met houvast',
    text: 'Collega’s noemen vooral duidelijkheid: waar totalen vandaan komen en hoe je ze kunt uitleggen aan opdrachtgevers.',
  },
  {
    Icon: FileSpreadsheet,
    title: 'Van dossier naar export',
    text: 'Veel teams combineren een interne uitdraai met een nette offerte of begroting richting de klant — uit dezelfde cijfers.',
  },
  {
    Icon: MessageCircle,
    title: 'Vragen? We helpen',
    text: 'Twijfel je of CalqTool bij jouw workflow past? Kijk op de pakkettenpagina of stel gerust een vraag via contact.',
  },
] as const;

export function Reviews() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-cms-reviews'],
    queryFn: () => api.getPublicCmsReviews(),
    staleTime: 60_000,
  });

  const cms = data?.reviews_cms;

  return (
    <div className="animate-page-enter w-full min-w-0">
      <SeoMeta
        path="/reviews"
        title="Reviews"
        description={`Lees wat bouwprofessionals en projectteams zeggen over werken met ${LEGAL_PRODUCT_NAME}: calculaties, dossiers en export. Ontdek of het bij jouw manier van werken past.`}
      />
      <AnimatedSection delay={0.05}>
        <div className="mx-auto w-full max-w-3xl text-left">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Reviews' }]} />
        </div>

        {isLoading && (
          <p className="mt-6 text-sm text-text-muted" role="status">
            Laden…
          </p>
        )}
        {isError && (
          <p
            className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
            role="alert"
          >
            Deze pagina kon niet worden geladen. Probeer het zo opnieuw of neem{' '}
            <Link to="/contact" className="font-medium text-primary hover:underline">
              contact
            </Link>{' '}
            met ons op.
          </p>
        )}

        {cms && (
          <div className="mt-6 w-full min-w-0 space-y-10 sm:space-y-14">
            {/* Hero — gelijnde kolom, leesbare regellengte op mobiel */}
            <header className="mx-auto w-full max-w-3xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-muted">
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                Ervaringen uit de praktijk
              </div>
              <h1 className="text-balance text-2xl font-bold tracking-tight text-text sm:text-3xl md:text-4xl">
                {cms.intro_heading}
              </h1>
              <p className="mt-4 text-pretty text-base leading-relaxed text-text-muted sm:text-lg">
                {cms.intro_lead}
              </p>
            </header>

            {/* Context — vaste bodytekst (zoals extra blokken op vergelijkbare landingspagina’s) */}
            <section
              className="mx-auto w-full max-w-3xl rounded-2xl border border-border bg-surface px-4 py-5 sm:px-6 sm:py-6"
              aria-labelledby="reviews-context-heading"
            >
              <h2 id="reviews-context-heading" className="text-base font-semibold text-text sm:text-lg">
                Waarom deze voorbeelden?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-text-muted sm:text-base">
                {LEGAL_PRODUCT_NAME} wordt gebruikt door aannemers, calculators, werkvoorbereiders en
                projectontwikkelaars — vooral bij renovatie, transformatie en woningsplitsing. Op deze pagina zetten we
                korte verhalen neer zodat je kunt meelezen hoe anderen het platform inzetten. Geen twee projecten zijn
                hetzelfde; jouw totaal en werkwijze kunnen dus anders uitpakken.
              </p>
            </section>

            {/* Drie pijlers — uitgelijnd grid, op mobiel één kolom volle breedte */}
            <section className="w-full" aria-labelledby="reviews-highlights-heading">
              <h2 id="reviews-highlights-heading" className="sr-only">
                Wat je op deze pagina vindt
              </h2>
              <ul className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-5">
                {HIGHLIGHTS.map(({ Icon, title, text }) => (
                  <li
                    key={title}
                    className="flex gap-3 rounded-xl border border-border bg-surface-card p-4 sm:flex-col sm:p-5"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-text sm:text-base">{title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-text-muted">{text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* Reviewkaarten */}
            <section className="w-full" aria-labelledby="reviews-cards-heading">
              <div className="mx-auto mb-5 max-w-3xl sm:mb-6">
                <h2 id="reviews-cards-heading" className="text-xl font-bold text-text sm:text-2xl">
                  Wat anderen zeggen
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-text-muted sm:text-base">
                  Een selectie van reacties — kort en concreet. Zo krijg je een beeld van het soort werk dat mensen met
                  het platform doen.
                </p>
              </div>

              <div className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:gap-8">
                {cms.reviews.map((r) => (
                  <article
                    key={r.id}
                    className="flex min-w-0 flex-col rounded-2xl border border-border bg-surface-card p-5 shadow-sm transition hover:border-primary/20 hover:shadow-md sm:p-6"
                  >
                    <blockquote className="min-w-0 flex-1 rounded-lg bg-surface px-3 py-3 text-sm leading-relaxed text-text sm:border-l-4 sm:border-primary/30 sm:bg-transparent sm:px-0 sm:pl-4 sm:py-0 sm:text-base sm:italic">
                      <span className="text-primary/80 sm:hidden" aria-hidden>
                        “
                      </span>
                      {r.quote}
                      <span className="text-primary/80 sm:hidden" aria-hidden>
                        ”
                      </span>
                    </blockquote>
                    <footer className="mt-5 flex min-w-0 items-center gap-3 border-t border-border pt-5 sm:mt-6 sm:pt-6">
                      <img
                        src={r.avatar_url}
                        alt=""
                        width={56}
                        height={56}
                        loading="lazy"
                        decoding="async"
                        referrerPolicy="no-referrer"
                        className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-border sm:h-14 sm:w-14"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold leading-snug text-text">{r.name}</p>
                        {r.role ? (
                          <p className="mt-0.5 text-sm leading-snug text-text-muted">{r.role}</p>
                        ) : null}
                      </div>
                    </footer>
                  </article>
                ))}
              </div>
            </section>

            {/* Meer weten — links, mobiel onder elkaar */}
            <section
              className="mx-auto w-full max-w-3xl rounded-2xl border border-dashed border-border bg-surface-card/50 px-4 py-5 sm:px-6 sm:py-6"
              aria-labelledby="reviews-more-heading"
            >
              <h2 id="reviews-more-heading" className="text-base font-semibold text-text">
                Meer weten?
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-text-muted">
                Bekijk pakketten en limieten, lees hoe export en offerte werken, of neem contact op voor een gerichte
                vraag.
              </p>
              <ul className="mt-4 flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-2">
                <li>
                  <Link to="/prijzen" className="font-medium text-primary hover:underline">
                    Pakketten &amp; prijzen
                  </Link>
                </li>
                <li>
                  <Link to="/help/export" className="font-medium text-primary hover:underline">
                    Export &amp; offerte (uitleg)
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="font-medium text-primary hover:underline">
                    Contact
                  </Link>
                </li>
              </ul>
            </section>

            {/* CTA — mobiel: knoppen volle breedte, netjes gestapeld */}
            <section className="mx-auto w-full max-w-2xl rounded-2xl border border-border bg-gradient-to-b from-surface to-surface-card px-4 py-8 text-center shadow-sm sm:px-8 sm:py-10">
              <h2 className="text-lg font-semibold text-text sm:text-xl">Zelf uitproberen?</h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-text-muted sm:text-base">
                Maak een account en start met een dossier — of mail ons als je eerst iets wilt afstemmen.
              </p>
              <div className="mt-6 flex w-full flex-col gap-3 sm:mx-auto sm:max-w-md sm:flex-row sm:justify-center sm:gap-3">
                <Link
                  to="/register"
                  className="inline-flex w-full items-center justify-center rounded-lg bg-primary px-5 py-3 text-sm font-medium text-white hover:bg-primary-alt sm:w-auto sm:min-w-[10rem]"
                >
                  Account aanmaken
                </Link>
                <Link
                  to="/login"
                  className="inline-flex w-full items-center justify-center rounded-lg border border-border bg-surface-card px-5 py-3 text-sm font-medium text-text hover:bg-surface sm:w-auto sm:min-w-[10rem]"
                >
                  Inloggen
                </Link>
              </div>
              <p className="mt-4">
                <Link to="/contact" className="text-sm font-medium text-primary hover:underline">
                  Contact opnemen
                </Link>
              </p>
            </section>

            <p className="mx-auto max-w-3xl text-center text-xs leading-relaxed text-text-muted sm:text-left">
              Ervaringen zijn illustratief; ze zijn geen offerte of garantie voor jouw project. Voor afspraken en
              aansprakelijkheid verwijzen we naar onze{' '}
              <Link to="/terms" className="text-primary hover:underline">
                algemene voorwaarden
              </Link>
              .
            </p>
          </div>
        )}
      </AnimatedSection>
    </div>
  );
}
